import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';
import { Category, NeedsSubcategory, FeelingScore, FEELING_EMOJIS } from '../types';
import Header from '../components/Header';

const CategorizeTransactions: React.FC = () => {
  const navigate = useNavigate();
  const {
    getUncategorizedTransactions,
    categorizeTransaction,
    categorizedTransactions,
    transactions,
  } = useApp();

  const uncategorized = getUncategorizedTransactions();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<NeedsSubcategory | null>(null);
  const [selectedFeeling, setSelectedFeeling] = useState<FeelingScore | null>(null);

  const currentTransaction = uncategorized[currentIndex];
  const totalExpenses = transactions.filter((t) => t.type !== 'income').length;
  const categorizedCount = categorizedTransactions.length;

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    if (category !== 'Needs') {
      setSelectedSubcategory(null);
    }
  };

  const handleSave = async () => {
    if (!currentTransaction || !selectedCategory || !selectedFeeling) return;
    if (selectedCategory === 'Needs' && !selectedSubcategory) return;

    await categorizeTransaction(
      currentTransaction.id,
      selectedCategory,
      selectedSubcategory || undefined,
      selectedFeeling
    );

    // Reset selections
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedFeeling(null);

    // Move to next transaction
    if (currentIndex < uncategorized.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleViewDashboard = () => {
    navigate('/mmt/dashboard');
  };

  if (uncategorized.length === 0) {
    return (
      <div className="min-h-screen bg-secondary">
        <Header />
        <div className="flex flex-col items-center justify-center p-6 min-h-[calc(100vh-80px)]">
          <div className="max-w-2xl text-center bg-white rounded-3xl p-12 shadow-xl">
            <h1 className="text-4xl font-bold mb-6">All Done!</h1>
            <p className="text-xl text-gray-700 mb-8">
              You've categorized all your transactions. View your personalized
              insights on the dashboard!
            </p>
            <button
              onClick={handleViewDashboard}
              className="bg-primary text-white px-12 py-4 rounded-full text-xl font-semibold hover:bg-primary/90 transition-all transform hover:scale-105"
            >
              View Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canSave =
    selectedCategory &&
    selectedFeeling &&
    (selectedCategory !== 'Needs' || selectedSubcategory);

  return (
    <div className="min-h-screen bg-secondary">
      <Header />
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-center mb-8">
            Categorize Transactions
          </h1>

          {/* Progress Indicator */}
          <div className="flex justify-center gap-8 mb-8">
            <div className="bg-white border-2 border-primary rounded-2xl px-8 py-4 shadow-lg">
              <p className="text-sm text-gray-600 uppercase tracking-wide font-semibold mb-1">
                Progress
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {categorizedCount} <span className="text-gray-400">/</span> {totalExpenses}
              </p>
              <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${(categorizedCount / totalExpenses) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {currentTransaction && (
            <div className="bg-white rounded-3xl p-8 shadow-xl mb-6 border-l-8 border-primary">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">
                    {currentTransaction.name}
                  </h2>
                  <p className="text-gray-700">
                    {new Date(currentTransaction.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-3xl font-bold">${currentTransaction.amount.toFixed(2)}</div>
              </div>

              <div className="space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm uppercase tracking-wide font-bold text-gray-700 mb-3">
                    Select Category
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(['Save', 'Needs', 'Wants', 'Debt'] as Category[]).map(
                      (category) => (
                        <button
                          key={category}
                          onClick={() => handleCategorySelect(category)}
                          className={`px-4 py-3 rounded-xl font-semibold transition-all shadow-md ${
                            selectedCategory === category
                              ? 'bg-primary text-white scale-105 shadow-lg'
                              : 'bg-secondary text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {category}
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Needs Subcategory */}
                {selectedCategory === 'Needs' && (
                  <div>
                    <label className="block text-sm uppercase tracking-wide font-bold text-gray-700 mb-3">
                      Need Type
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {(
                        ['Housing', 'Food', 'Transportation', 'Self Care'] as NeedsSubcategory[]
                      ).map((subcategory) => (
                        <button
                          key={subcategory}
                          onClick={() => setSelectedSubcategory(subcategory)}
                          className={`px-4 py-3 rounded-xl font-semibold transition-all shadow-md ${
                            selectedSubcategory === subcategory
                              ? 'bg-accent text-white scale-105 shadow-lg'
                              : 'bg-secondary text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {subcategory}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feeling Meter */}
                <div>
                  <label className="block text-sm uppercase tracking-wide font-bold text-gray-700 mb-3">
                    Feeling Meter
                  </label>
                  <div className="flex justify-around items-center bg-secondary rounded-2xl p-6 shadow-inner">
                    {([1, 2, 3, 4, 5] as FeelingScore[]).map((score) => (
                      <button
                        key={score}
                        onClick={() => setSelectedFeeling(score)}
                        className={`text-5xl transition-all transform hover:scale-125 ${
                          selectedFeeling === score ? 'scale-125' : ''
                        }`}
                        title={`Score: ${score}`}
                      >
                        {FEELING_EMOJIS[score]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-center pt-4">
                  <button
                    onClick={handleSave}
                    disabled={!canSave}
                    className={`px-12 py-4 rounded-full text-xl font-semibold transition-all ${
                      canSave
                        ? 'bg-primary text-white hover:bg-primary/90 transform hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Save & Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
              disabled={currentIndex === 0}
              className="px-6 py-3 rounded-full font-semibold bg-white border-2 border-gray-200 hover:border-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
            >
              ← Previous
            </button>

            <button
              onClick={handleViewDashboard}
              className="px-6 py-3 rounded-full font-semibold bg-accent text-white hover:bg-accent/90 transition-all shadow-md"
            >
              Skip to Dashboard
            </button>

            <button
              onClick={() =>
                setCurrentIndex(Math.min(uncategorized.length - 1, currentIndex + 1))
              }
              disabled={currentIndex === uncategorized.length - 1}
              className="px-6 py-3 rounded-full font-semibold bg-white border-2 border-gray-200 hover:border-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategorizeTransactions;
